import React, { useState, useEffect } from 'react';
import { useLanguage } from '../hooks/useLanguage';
import { userApi } from '../services/api';
import toast from 'react-hot-toast';

interface UserAddress {
  address: string;
  city: string;
  postalCode: string;
  country: string;
  phone: string;
}

interface AddressFormProps {
  user: {
    address?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    phone?: string;
  };
  onUpdate: () => void;
}

export const AddressForm: React.FC<AddressFormProps> = ({ user, onUpdate }) => {
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<UserAddress>({
    address: user.address || '',
    city: user.city || '',
    postalCode: user.postalCode || '',
    country: user.country || 'Italia',
    phone: user.phone || ''
  });

  const handleInputChange = (field: keyof UserAddress, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await userApi.updateProfile(formData);
      toast.success(t('account.addressUpdated'));
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating address:', error);
      toast.error(t('account.addressUpdateError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      address: user.address || '',
      city: user.city || '',
      postalCode: user.postalCode || '',
      country: user.country || 'Italia',
      phone: user.phone || ''
    });
    setIsEditing(false);
  };

  const hasAddressData = user.address || user.city || user.postalCode || user.country || user.phone;

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {t('account.personalAddress')}
        </h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            {hasAddressData ? t('account.edit') : t('account.add')}
          </button>
        )}
      </div>

      {!isEditing ? (
        // Display mode aligned like the edit layout
        <div className="space-y-4">
          {hasAddressData ? (
            <>
              {user.address && (
                <div>
                  <p className="text-sm text-gray-600">{t('account.address')}</p>
                  <p className="text-gray-900">{user.address}</p>
                </div>
              )}

              {(user.city || user.postalCode) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {user.city && (
                    <div>
                      <p className="text-sm text-gray-600">{t('account.city')}</p>
                      <p className="text-gray-900">{user.city}</p>
                    </div>
                  )}
                  {user.postalCode && (
                    <div>
                      <p className="text-sm text-gray-600">{t('account.postalCode')}</p>
                      <p className="text-gray-900">{user.postalCode}</p>
                    </div>
                  )}
                </div>
              )}

              {(user.country || user.phone) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {user.country && (
                    <div>
                      <p className="text-sm text-gray-600">{t('account.country')}</p>
                      <p className="text-gray-900">{user.country}</p>
                    </div>
                  )}
                  {user.phone && (
                    <div>
                      <p className="text-sm text-gray-600">{t('account.phone')}</p>
                      <p className="text-gray-900">{user.phone}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-gray-500 italic">
              {t('account.noAddressData')}
            </p>
          )}
        </div>
      ) : (
        // Edit mode
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('account.address')} *
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('account.city')} *
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('account.postalCode')} *
              </label>
              <input
                type="text"
                value={formData.postalCode}
                onChange={(e) => handleInputChange('postalCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('account.country')} *
              </label>
              <select
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="Italia">Italia</option>
                <option value="Francia">Francia</option>
                <option value="Germania">Germania</option>
                <option value="Spagna">Spagna</option>
                <option value="Svizzera">Svizzera</option>
                <option value="Austria">Austria</option>
                <option value="Altro">Altro</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('account.phone')}
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              onClick={handleCancel}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {t('account.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !formData.address || !formData.city || !formData.postalCode || !formData.country}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? t('account.saving') : t('account.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}; 